import Phaser from 'phaser';
import { TileAnimations } from '../animations/TileAnimations';
import { ITile, PipValue } from '../core/types';
import { COLORS, TILE } from '../utils/Constants';

export class DominoTile extends Phaser.GameObjects.Container {
  public tileData: ITile;
  public isPlayable: boolean = false;
  public isSelected: boolean = false;
  
  private bg: Phaser.GameObjects.Graphics;
  private topDots: Phaser.GameObjects.Graphics;
  private bottomDots: Phaser.GameObjects.Graphics;
  private dividerLine: Phaser.GameObjects.Graphics;
  private glowEffect: Phaser.GameObjects.Graphics;
  
  private isHorizontal: boolean;

  constructor(scene: Phaser.Scene, x: number, y: number, tileData: ITile, isHorizontal = false) {
    super(scene, x, y);
    this.tileData = tileData;
    this.isHorizontal = isHorizontal;

    this.glowEffect = this.scene.add.graphics();
    this.bg = this.scene.add.graphics();
    this.dividerLine = this.scene.add.graphics();
    this.topDots = this.scene.add.graphics();
    this.bottomDots = this.scene.add.graphics();

    this.add([this.glowEffect, this.bg, this.dividerLine, this.topDots, this.bottomDots]);

    this.drawTile();

    const w = this.isHorizontal ? TILE.HEIGHT : TILE.WIDTH;
    const h = this.isHorizontal ? TILE.WIDTH : TILE.HEIGHT;
    this.setSize(w, h);
    
    this.setInteractive({ useHandCursor: true });
    
    this.on('pointerover', this.animateHover, this);
    this.on('pointerout', this.animateUnhover, this);
  }

  public drawTile() {
    this.bg.clear();
    this.topDots.clear();
    this.bottomDots.clear();
    this.dividerLine.clear();
    this.glowEffect.clear();

    const tW = TILE?.WIDTH || 50;
    const tH = TILE?.HEIGHT || 90;

    const w = this.isHorizontal ? tH : tW;
    const h = this.isHorizontal ? tW : tH;
    const r = 8;
    const hw = w / 2;
    const hh = h / 2;
    
    // Shadow
    this.bg.fillStyle(0x000000, 0.4);
    this.bg.fillRoundedRect(-hw + 2, -hh + 4, w, h, r);
    
    // Main tile bg
    this.bg.fillStyle(COLORS.TILE_WHITE, 1);
    this.bg.fillRoundedRect(-hw, -hh, w, h, r);
    
    // Border
    this.bg.lineStyle(2, COLORS?.GOLD || 0xd4af37, 1);
    this.bg.strokeRoundedRect(-hw, -hh, w, h, r);

    // Divider
    this.dividerLine.lineStyle(2, COLORS?.GOLD || 0xd4af37, 1);
    if (this.isHorizontal) {
      this.dividerLine.beginPath();
      this.dividerLine.moveTo(0, -hh + 2);
      this.dividerLine.lineTo(0, hh - 2);
      this.dividerLine.strokePath();
      
      this.drawDots(this.topDots, this.tileData.a, -w / 4, 0, 3.5);
      this.drawDots(this.bottomDots, this.tileData.b, w / 4, 0, 3.5);
    } else {
      this.dividerLine.beginPath();
      this.dividerLine.moveTo(-hw + 2, 0);
      this.dividerLine.lineTo(hw - 2, 0);
      this.dividerLine.strokePath();
      
      this.drawDots(this.topDots, this.tileData.a, 0, -h / 4, 3.5);
      this.drawDots(this.bottomDots, this.tileData.b, 0, h / 4, 3.5);
    }

    if (this.isPlayable) {
      this.glowEffect.lineStyle(4, COLORS.ACCENT_GREEN, 0.8);
      this.glowEffect.strokeRoundedRect(-hw - 2, -hh - 2, w + 4, h + 4, r + 2);
    }
    
    if (this.isSelected) {
      this.glowEffect.lineStyle(4, COLORS?.GOLD || 0xd4af37, 1);
      this.glowEffect.strokeRoundedRect(-hw - 2, -hh - 2, w + 4, h + 4, r + 2);
    }
  }

  private drawDots(graphics: Phaser.GameObjects.Graphics, value: PipValue, cx: number, cy: number, radius: number) {
    graphics.fillStyle(0x333333, 1);
    
    const d = 12; 

    const drawDot = (dx: number, dy: number) => {
      graphics.fillCircle(cx + dx, cy + dy, radius);
      
      // Inner subtle highlight for 3D effect
      graphics.fillStyle(0x000000, 0.5);
      graphics.fillCircle(cx + dx, cy + dy, radius * 0.5);
      graphics.fillStyle(0x333333, 1);
    };

    switch(value) {
      case 1:
        drawDot(0, 0);
        break;
      case 2:
        drawDot(-d, -d); drawDot(d, d);
        break;
      case 3:
        drawDot(-d, -d); drawDot(0, 0); drawDot(d, d);
        break;
      case 4:
        drawDot(-d, -d); drawDot(d, -d);
        drawDot(-d, d); drawDot(d, d);
        break;
      case 5:
        drawDot(-d, -d); drawDot(d, -d);
        drawDot(0, 0);
        drawDot(-d, d); drawDot(d, d);
        break;
      case 6:
        drawDot(-d, -d); drawDot(0, -d); drawDot(d, -d);
        drawDot(-d, d); drawDot(0, d); drawDot(d, d);
        break;
    }
  }

  public setPlayable(playable: boolean) {
    this.isPlayable = playable;
    this.drawTile();
  }

  public setSelected(selected: boolean) {
    this.isSelected = selected;
    this.drawTile();
  }

  public animateHover() {
    if (this.isPlayable || (this.scene.sys.game as any).config.gameTitle) {
      TileAnimations.hoverUp(this.scene, this);
    }
  }

  public animateUnhover() {
    TileAnimations.hoverDown(this.scene, this);
  }

  public animatePlay(targetX: number, targetY: number) {
    TileAnimations.playToBoard(this.scene, this, targetX, targetY);
  }

  public animateDeal(fromX: number, fromY: number, toX: number, toY: number, delay: number) {
    TileAnimations.dealToHand(this.scene, this, fromX, fromY, toX, toY, delay);
  }

  public animateShake() {
    TileAnimations.shake(this.scene, this);
  }

  public setHorizontal(horizontal: boolean) {
    this.isHorizontal = horizontal;
    const tW = TILE?.WIDTH || 50;
    const tH = TILE?.HEIGHT || 90;
    const w = this.isHorizontal ? tH : tW;
    const h = this.isHorizontal ? tW : tH;
    this.setSize(w, h);
    this.drawTile();
  }
}
